import {Component, Input, OnInit} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {ClientConfiguration} from '@twpub/core/utils';
import {NGXLogger} from 'ngx-logger';
import {CssVariableDefinition} from '@twpub/core/models';
import {CssService} from '@twpub/core/services/css.service';

import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatOptgroup, MatOption, MatSelect} from '@angular/material/select';
import {MatInput} from '@angular/material/input';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';

@Component({
  selector: 'pub-css-variables-editor',
  templateUrl: './css-variables-editor.component.html',
  styleUrls: ['./css-variables-editor.component.scss'],
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, MatFormField, MatLabel, MatSelect, MatOptgroup, MatOption, MatInput, MatIconButton, MatIcon, MatButton]
})
export class CssVariablesEditorComponent implements OnInit {
  @Input() cssVariables: { [key: string]: string } = {};
  @Input() editConfiguration!: ClientConfiguration;

  form!: FormGroup;
  isUpdating = false;

  availableCssVariables: CssVariableDefinition[] = [];

  constructor(private logger: NGXLogger, private fb: FormBuilder, private cssService: CssService) {
  }

  ngOnInit(): void {
    this.logger.debug('CssVariablesEditorComponent.ngOnInit');
    this.form = this.fb.group({
      variables: this.fb.array([])
    });
    this.availableCssVariables = this.cssService.getAvailableCssVariables().filter((cssVar) => cssVar.category !== 'Typography');
    this.populateForm();
  }

  private populateForm(): void {
    this.logger.debug('CssVariablesEditorComponent.populateForm');
    this.isUpdating = true;

    // Clear the array first
    while (this.variablesFormArray.length) {
      this.variablesFormArray.removeAt(0);
    }

    if (this.editConfiguration.cssVariables && Object.keys(this.editConfiguration.cssVariables).length) {
      Object.entries(this.editConfiguration.cssVariables).forEach(([variable, value]) => {
        if (variable !== '--tw-font-family') {
          this.variablesFormArray.push(
            this.createVariableFormGroup(variable, value)
          );
        }
      });
    } else {
      this.addVariableRow();
    }

    // Listen for form changes
    setTimeout(() => {
      this.isUpdating = false;
      this.form.valueChanges.subscribe(() => {
        if (!this.isUpdating) {
          this.updateConfiguration();
        }
      });
    });
  }

  getPlaceholder(variable: string | null): string {
    if (!variable) return '';
    const defaultValue = this.getDefaultValue(variable);
    return `Default (${defaultValue})`;
  }

  get variablesFormArray(): FormArray {
    return this.form.get('variables') as FormArray;
  }

  createVariableFormGroup(variable: string = '', value: string = ''): FormGroup {
    return this.fb.group({
      variable: [variable, Validators.required],
      value: [value]
    });
  }

  addVariableRow(): void {
    this.variablesFormArray.push(this.createVariableFormGroup());
  }

  removeVariableRow(index: number): void {
    this.variablesFormArray.removeAt(index);
    this.updateConfiguration();
  }

  updateConfiguration(): void {
    if (!this.form.valid || !this.editConfiguration) return;

    this.isUpdating = true;
    try {
      const result: { [key: string]: string } = {};
      this.variablesFormArray.controls.forEach(control => {
        const variable = control.get('variable')?.value;
        const value = control.get('value')?.value;
        if (variable && value?.length) {
          result[variable] = value;
        }
      });

      this.logger.debug('Updating cssVariables:', result);
      this.editConfiguration.cssVariables = result;
    } finally {
      this.isUpdating = false;
    }
  }

  getFilteredVariables(category: string, currentIndex: number): CssVariableDefinition[] {
    const selectedVariables = new Set(
      this.variablesFormArray.controls
        .map((control, i) => (i !== currentIndex ? control.get('variable')?.value : null))
        .filter(Boolean)
    );

    return this.availableCssVariables.filter(v =>
      v.category === category && !selectedVariables.has(v.variable)
    );
  }

  getDefaultValue(variable: string): string {
    const found = this.availableCssVariables.find(v => v.variable === variable);
    return found ? found.defaultValue : '';
  }
}
